import { useMutation } from '@tanstack/react-query'
import {
  proceduresApi,
  type AssignProcedureResult,
  type StepAssignment,
} from '@/api/proceduresApi'

/**
 * Assign To, as mutations.
 *
 * Thin on purpose — the callers keep their own saving/error state, the journey
 * reload and the step-execution invalidation, all of which are ordered
 * deliberately (see `saveProcedureAssign`). These wrappers add no behaviour of
 * their own.
 *
 * What they add is membership. Both calls used to go straight to
 * `proceduresApi` from the page, which is two things at once: against the
 * repo's rule that a backend call is exposed through a TanStack hook rather
 * than called from a component, and — because the audit trail is refreshed by
 * a cache-wide rule on successful MUTATIONS (`src/lib/queryClient.ts`) — the
 * reason an assignment was the one audited action that did not appear in the
 * trail until the panel was reopened.
 *
 * Routing them through `useMutation` fixes both, and any assignment surface
 * added later inherits the refresh instead of having to remember it.
 */

/** Procedure-level cascade: assigns, or with null clears, EVERY form of the run. */
export function useAssignProcedure() {
  return useMutation<AssignProcedureResult, Error, { id: string; assigneeId: string | null }>({
    mutationFn: ({ id, assigneeId }) => proceduresApi.assignProcedure(id, assigneeId),
  })
}

/** Assign To for one form step of the procedure (null unassigns). */
export function useAssignStep() {
  return useMutation<
    StepAssignment & { lockedSteps: number },
    Error,
    { id: string; stepId: string; assigneeId: string | null }
  >({
    mutationFn: ({ id, stepId, assigneeId }) =>
      proceduresApi.assignStep(id, stepId, assigneeId),
  })
}
